import type { ComponentType, SVGProps } from "react";
import { AiIcon, DesignIcon, MobileIcon, ProductIcon } from "./booking-icons";

export type ServiceId = "uiux" | "web" | "ai" | "product";

export const SERVICES: {
  id: ServiceId;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
}[] = [
  { id: "uiux", icon: DesignIcon },
  { id: "web", icon: MobileIcon },
  { id: "ai", icon: AiIcon },
  { id: "product", icon: ProductIcon },
];

export const DEFAULT_SERVICE: ServiceId = "web";

import {
  SLOT_TIMES,
  isPast,
  toDayKey,
  type DayKey,
} from "@/lib/booking-slots";

/** 24h values, index-aligned with the `Booking.timeSlots` messages array. */
export const TIME_SLOT_VALUES: readonly string[] = SLOT_TIMES;

/** What the availability endpoint tells us, once it has answered. */
export type Availability = {
  /** Slots already booked, as { "YYYY-MM-DD": ["09:00", …] }. */
  taken: Record<string, string[]>;
  /** "Now" on a Riyadh clock, so the visitor's own clock is never trusted. */
  now: { day: DayKey; time: string };
};

/** Week starts on Saturday in Arabic locales, Sunday elsewhere. */
export function getWeekStart(locale: string): number {
  return locale === "ar" ? 6 : 0;
}

export function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function isSameDay(a: Date | null, b: Date | null): boolean {
  return (
    !!a &&
    !!b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function addMonths(date: Date, amount: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + amount, 1);
}

/** Bookable days: not in the past and not a Friday (weekend). */
export function isDateAvailable(date: Date, today: Date): boolean {
  return date.getTime() >= startOfDay(today).getTime() && date.getDay() !== 5;
}

/**
 * A slot is open when nobody holds it and it has not already passed. Until the
 * availability endpoint answers, every slot is treated as open: the server
 * revalidates on submit, so an optimistic calendar can only ever cost a
 * refused booking, never a double one.
 */
export function isSlotAvailable(
  date: Date | null,
  slotIndex: number,
  availability: Availability | null,
): boolean {
  if (!date) {
    return false;
  }

  const time = TIME_SLOT_VALUES[slotIndex];
  if (!time) {
    return false;
  }

  if (!availability) {
    return true;
  }

  const day = toDayKey(date);

  if (isPast(day, time, availability.now)) {
    return false;
  }

  return !(availability.taken[day] ?? []).includes(time);
}

/** A day with no open slot left is not worth offering. */
export function hasOpenSlot(
  date: Date,
  availability: Availability | null,
): boolean {
  return TIME_SLOT_VALUES.some((_, index) =>
    isSlotAvailable(date, index, availability),
  );
}

export function findFirstAvailableDate(
  today: Date,
  availability: Availability | null = null,
): Date {
  const candidate = startOfDay(today);

  for (let offset = 0; offset < 60; offset += 1) {
    const date = new Date(
      candidate.getFullYear(),
      candidate.getMonth(),
      candidate.getDate() + offset,
    );

    if (isDateAvailable(date, today) && hasOpenSlot(date, availability)) {
      return date;
    }
  }

  return candidate;
}
